import { type Dayjs } from "dayjs";

import { type ProductChargeTypeEnum, type ProductTypeEnum } from "@fitness-app/data-models/entities/Product";

import { type ResolvedRange } from "~/modules/Dashboard/AppStats/hooks/comparisonRange";

/**
 * The business splits paid products into three tiers. A free product of one of those types is sold
 * as a trial and gets a tier of its own. Add-ons ride on top of a client
 * who is already counted, and a free add-on is a lead magnet, so neither is a "new client".
 */
export const PAID_SEGMENTS = ["highTicket", "lowTicket", "content"] as const;

export const NEW_CLIENT_SEGMENTS = [...PAID_SEGMENTS, "trial"] as const;

export type NewClientSegment = (typeof NEW_CLIENT_SEGMENTS)[number];

const PAID_SEGMENT_BY_PRODUCT_TYPE: Partial<Record<ProductTypeEnum, NewClientSegment>> = {
  PERSONAL_CLIENT_SERVICE: "highTicket",
  AUTOMATED_CLIENT_SERVICE: "lowTicket",
  ONLY_CONTENT: "content",
};

const segmentOf = (row: NewClientsMonthStatsRow): NewClientSegment | undefined => {
  // The embedded product is nullable: RLS may hide the row while the stats row stays readable.
  const paidSegment = row.productData ? PAID_SEGMENT_BY_PRODUCT_TYPE[row.productData.type] : undefined;

  if (!paidSegment) {
    return undefined;
  }

  return row.type === "FREE" ? "trial" : paidSegment;
};

/** The slice of `product_month_stats` the chart reads; `byDay` keys are "D-M". */
export interface NewClientsMonthStatsRow {
  monthYear: string;
  product: string;
  type: ProductChargeTypeEnum;
  byDay: Record<string, { clients?: number } | undefined>;
  productData: { name: string; type: ProductTypeEnum } | null;
}

export type SegmentTotals = Record<NewClientSegment, number>;

export interface NewClientsDay extends SegmentTotals {
  /** ISO date, e.g. "2026-09-04". */
  dateKey: string;
  /** Short label for the axis, e.g. "04.09". */
  label: string;
  total: number;
  byProduct: Record<string, number>;
}

export interface NewClientsProduct {
  id: string;
  name: string;
  segment: NewClientSegment;
  total: number;
}

export interface NewClientsAggregate {
  /** One row per day of the range, zero-filled. */
  daily: NewClientsDay[];
  totals: SegmentTotals;
  total: number;
  /** Products with at least one client in the range, most clients first. */
  products: NewClientsProduct[];
}

const emptyTotals = (): SegmentTotals => ({ highTicket: 0, lowTicket: 0, content: 0, trial: 0 });

const monthKey = (date: Dayjs) => `${date.year()}-${date.month() + 1}`;
const dayKey = (date: Dayjs) => `${date.date()}-${date.month() + 1}`;

/** "YYYY-M" keys of every month the range touches, in order. */
export const monthKeysOfRange = ([start, end]: ResolvedRange): string[] => {
  const keys: string[] = [];

  for (let cursor = start.startOf("month"); !cursor.isAfter(end); cursor = cursor.add(1, "month")) {
    keys.push(monthKey(cursor));
  }

  return keys;
};

/**
 * Folds product-month stats into per-day new-client counts per tier and per product for every day
 * of the range. `productIds` narrows the
 * count to the selected products; null means every product.
 */
export const aggregateNewClients = (
  stats: NewClientsMonthStatsRow[],
  range: ResolvedRange,
  productIds: ReadonlySet<string> | null,
): NewClientsAggregate => {
  const products = new Map<string, NewClientsProduct>();
  const rowsByMonth = new Map<string, NewClientsMonthStatsRow[]>();

  for (const row of stats) {
    const segment = segmentOf(row);

    if (!segment || (productIds && !productIds.has(row.product))) {
      continue;
    }

    products.set(row.product, { id: row.product, name: row.productData?.name ?? row.product, segment, total: 0 });
    rowsByMonth.set(row.monthYear, [...(rowsByMonth.get(row.monthYear) ?? []), row]);
  }

  const daily: NewClientsDay[] = [];
  const [start, end] = range;

  for (let cursor = start.startOf("day"); !cursor.isAfter(end); cursor = cursor.add(1, "day")) {
    const day: NewClientsDay = {
      dateKey: cursor.format("YYYY-MM-DD"),
      label: cursor.format("DD.MM"),
      ...emptyTotals(),
      total: 0,
      byProduct: {},
    };
    const key = dayKey(cursor);

    for (const row of rowsByMonth.get(monthKey(cursor)) ?? []) {
      const clients = row.byDay[key]?.clients ?? 0;

      if (!clients) {
        continue;
      }

      const product = products.get(row.product)!;
      day[product.segment] += clients;
      day.total += clients;
      day.byProduct[row.product] = (day.byProduct[row.product] ?? 0) + clients;
      product.total += clients;
    }

    daily.push(day);
  }

  const totals = daily.reduce((acc, day) => {
    for (const segment of NEW_CLIENT_SEGMENTS) {
      acc[segment] += day[segment];
    }
    return acc;
  }, emptyTotals());

  return {
    daily,
    totals,
    total: daily.reduce((acc, day) => acc + day.total, 0),
    products: [...products.values()].filter((product) => product.total > 0).sort((a, b) => b.total - a.total),
  };
};
